import { useState, useCallback, useMemo } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { useApiMutation } from '../hooks/useApiMutation'
import { useToast } from '../components/ToastContainer'
import { apiFetch, webUrl } from '../lib/api'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Select } from '../ui/Select'
import { Textarea } from '../ui/Textarea'
import { Modal } from '../ui/Modal'
import { Badge } from '../ui/Badge'
import { Spinner } from '../ui/Spinner'
import { FileUpload } from '../ui/FileUpload'
import { AdminTable } from '../ui/AdminTable'
import type { TableColumn } from '../ui/Table'
import styles from './SharedMusic.module.css'

// ── 타입 ──

interface SharedAsset {
  id: string
  type: string
  name: string
  slug: string
  category: string
  tags: string[]
  description: string | null
  s3Key: string | null
  fileUrl: string | null
  fileSize: number | null
  mimeType: string | null
  metadata: Record<string, unknown>
  sortOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface AssetsResponse {
  assets: SharedAsset[]
}

// ── 상수 ──

const CATEGORY_OPTIONS = [
  { value: 'classic', label: '클래식' },
  { value: 'pop', label: '팝/뉴에이지' },
  { value: 'jazz', label: '재즈' },
  { value: 'acoustic', label: '어쿠스틱' },
  { value: 'korean', label: '국악/한국' },
  { value: 'other', label: '기타' },
]

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: '전체' },
  ...CATEGORY_OPTIONS,
]

const CATEGORY_BADGE_COLORS: Record<string, 'blue' | 'green' | 'red' | 'yellow'> = {
  classic: 'blue',
  pop: 'green',
  jazz: 'yellow',
  acoustic: 'blue',
  korean: 'red',
  other: 'blue',
}

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
)

const LICENSE_OPTIONS = [
  { value: '', label: '미지정' },
  { value: 'CC0', label: 'CC0 (퍼블릭 도메인)' },
  { value: 'CC-BY', label: 'CC-BY (어트리뷰션 필요)' },
  { value: 'royalty-free', label: '로열티프리' },
  { value: 'public-domain', label: '저작권 만료/공공' },
  { value: 'custom', label: '직접 라이선스 보유' },
]

const LICENSE_LABELS: Record<string, string> = Object.fromEntries(
  LICENSE_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
)

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// ── 메인 컴포넌트 ──

export function SharedMusic() {
  const { addToast } = useToast()

  const { data, isLoading, refetch } = useApiFetch<AssetsResponse>(
    '/api/admin/shared-assets?type=music&includeInactive=true',
  )
  const assetsList = data?.assets ?? []

  const [categoryFilter, setCategoryFilter] = useState('')
  const filteredAssets = useMemo(() => {
    if (!categoryFilter) return assetsList
    return assetsList.filter((a) => a.category === categoryFilter)
  }, [assetsList, categoryFilter])

  // 업로드
  const [uploading, setUploading] = useState(false)
  const [uploadKey, setUploadKey] = useState(0)

  // 편집 모달
  const [editingAsset, setEditingAsset] = useState<SharedAsset | null>(null)
  const [formName, setFormName] = useState('')
  const [formSlug, setFormSlug] = useState('')
  const [formCategory, setFormCategory] = useState('other')
  const [formTags, setFormTags] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formSortOrder, setFormSortOrder] = useState(0)
  const [formArtist, setFormArtist] = useState('')
  const [formLicense, setFormLicense] = useState('')
  const [formSource, setFormSource] = useState('')
  const [formAttribution, setFormAttribution] = useState('')

  const updateMut = useApiMutation<SharedAsset, Record<string, unknown>>('/api/admin/shared-assets', 'PUT')
  const toggleMut = useApiMutation<unknown, Record<string, unknown>>('/api/admin/shared-assets', 'PATCH')

  // 파일 업로드 → 자동 등록
  const handleUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return
    setUploading(true)
    try {
      for (const file of files) {
        const formData = new FormData()
        formData.append('file', file)
        await apiFetch<SharedAsset>(
          '/api/admin/shared-assets/upload?type=music',
          { method: 'POST', body: formData },
        )
      }
      addToast({ type: 'success', message: `${files.length}개 음악이 등록되었습니다` })
      setUploadKey((k) => k + 1)
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '업로드 실패' })
    } finally {
      setUploading(false)
    }
  }, [addToast, refetch])

  // 편집
  const openEdit = useCallback((asset: SharedAsset) => {
    setEditingAsset(asset)
    setFormName(asset.name)
    setFormSlug(asset.slug)
    setFormCategory(asset.category)
    setFormTags((asset.tags ?? []).join(', '))
    setFormDescription(asset.description ?? '')
    setFormSortOrder(asset.sortOrder)
    setFormArtist((asset.metadata?.artist as string) ?? '')
    setFormLicense((asset.metadata?.license as string) ?? '')
    setFormSource((asset.metadata?.source as string) ?? '')
    setFormAttribution((asset.metadata?.attribution as string) ?? '')
  }, [])

  const handleSave = useCallback(async () => {
    if (!editingAsset) return
    if (!formLicense || !formSource.trim()) {
      addToast({ type: 'error', message: '라이선스와 출처는 필수입니다 (저작권 추적용)' })
      return
    }
    const tags = formTags.split(',').map(t => t.trim()).filter(Boolean)
    const result = await updateMut.trigger({
      name: formName,
      slug: formSlug,
      category: formCategory,
      tags,
      description: formDescription || null,
      sortOrder: formSortOrder,
      metadata: {
        ...editingAsset.metadata,
        artist: formArtist || undefined,
        license: formLicense || undefined,
        source: formSource.trim() || undefined,
        attribution: formAttribution.trim() || undefined,
      },
    }, { pathOverride: `/api/admin/shared-assets/${editingAsset.id}` })
    if (result) {
      addToast({ type: 'success', message: '수정되었습니다' })
      setEditingAsset(null)
      refetch()
    }
  }, [editingAsset, formName, formSlug, formCategory, formTags, formDescription, formSortOrder, formArtist, formLicense, formSource, formAttribution, updateMut, addToast, refetch])

  const handleToggle = useCallback(async (row: SharedAsset, _newValue: boolean) => {
    const result = await toggleMut.trigger({}, { pathOverride: `/api/admin/shared-assets/${row.id}/toggle` })
    if (result === null) throw new Error('토글 변경에 실패했습니다')
    refetch()
  }, [toggleMut, refetch])

  const handleDelete = useCallback(async (row: SharedAsset) => {
    await apiFetch(`/api/admin/shared-assets/${row.id}`, { method: 'DELETE' })
    refetch()
  }, [refetch])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px' }}>
        <Spinner />
      </div>
    )
  }

  const columns: TableColumn<SharedAsset>[] = [
    {
      key: 'name',
      label: '이름',
      render: (row) => (
        <>
          <div style={{ fontWeight: 600 }}>{row.name}</div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{row.slug}</div>
        </>
      ),
    },
    {
      key: 'category',
      label: '카테고리',
      width: '110px',
      render: (row) => (
        <Badge color={CATEGORY_BADGE_COLORS[row.category] ?? 'blue'}>
          {CATEGORY_LABELS[row.category] ?? row.category}
        </Badge>
      ),
    },
    {
      key: 'license',
      label: '라이선스',
      width: '140px',
      render: (row) => {
        const license = row.metadata?.license as string | undefined
        if (!license) {
          return <Badge color="red">미지정 ⚠</Badge>
        }
        return <Badge color="green">{LICENSE_LABELS[license] ?? license}</Badge>
      },
    },
    {
      key: 'fileSize',
      label: '크기',
      width: '80px',
      render: (row) => <span className={styles.fileInfo}>{formatFileSize(row.fileSize)}</span>,
    },
    {
      key: 'player',
      label: '재생',
      width: '200px',
      render: (row) => row.fileUrl ? (
        <audio controls className={styles.audioPlayer} preload="none">
          <source src={row.fileUrl} type={row.mimeType || 'audio/mpeg'} />
        </audio>
      ) : null,
    },
    {
      key: 'url',
      label: 'URL',
      width: '60px',
      render: (row) => (
        <button
          type="button"
          className={styles.copyUrlBtn}
          onClick={() => {
            navigator.clipboard.writeText(webUrl(`/assets/music/${row.slug}`))
            addToast({ type: 'success', message: 'URL이 복사되었습니다' })
          }}
          aria-label="URL 복사"
        >
          📋
        </button>
      ),
    },
  ]

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>음악 에셋</h2>
        <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} options={CATEGORY_FILTER_OPTIONS} size="sm" />
      </div>

      {/* 업로드 영역 */}
      <div style={{ marginBottom: '24px' }}>
        <FileUpload
          key={uploadKey}
          accept=".mp3,.m4a"
          placeholder=".mp3, .m4a 파일을 드래그하거나 클릭 (자동 등록)"
          onChange={handleUpload}
          loading={uploading}
          multiple
        />
      </div>

      <AdminTable<SharedAsset>
        columns={columns}
        data={filteredAssets}
        rowKey={(row) => row.id}
        emptyMessage="등록된 음악이 없습니다"
        hoverable
        toggles={[{ label: '활성', onToggle: handleToggle, getValue: (row) => row.isActive }]}
        actions={[{ label: '편집', onClick: openEdit }]}
        delete={{
          message: (row) => `"${row.name}" 음악을 삭제하시겠습니까?`,
          onDelete: handleDelete,
          successMessage: '삭제되었습니다',
        }}
      />

      {/* 편집 모달 */}
      <Modal open={!!editingAsset} onClose={() => setEditingAsset(null)} title="음악 편집" size="lg">
        <div className={styles.formGrid}>
          {editingAsset?.fileUrl && (
            <div className={styles.formFull}>
              <audio controls style={{ width: '100%' }} preload="none">
                <source src={editingAsset.fileUrl} type={editingAsset.mimeType || 'audio/mpeg'} />
              </audio>
            </div>
          )}
          <Input label="이름" value={formName} onChange={(e) => setFormName(e.target.value)} />
          <Input label="Slug" value={formSlug} onChange={(e) => setFormSlug(e.target.value)} />
          <Select label="카테고리" value={formCategory} onChange={(e) => setFormCategory(e.target.value)} options={CATEGORY_OPTIONS} />
          <Input label="정렬 순서" type="number" value={String(formSortOrder)} onChange={(e) => setFormSortOrder(Number(e.target.value))} />
          <div className={styles.formFull}>
            <Input label="아티스트" value={formArtist} onChange={(e) => setFormArtist(e.target.value)} placeholder="아티스트 이름" />
          </div>
          <Select
            label="라이선스 *"
            value={formLicense}
            onChange={(e) => setFormLicense(e.target.value)}
            options={LICENSE_OPTIONS}
          />
          <Input
            label="출처 *"
            value={formSource}
            onChange={(e) => setFormSource(e.target.value)}
            placeholder="예: Pixabay Music, YouTube Audio Library"
          />
          <div className={styles.formFull}>
            <Textarea
              label="어트리뷰션 표시 문구 (CC-BY 등)"
              value={formAttribution}
              onChange={(e) => setFormAttribution(e.target.value)}
              rows={2}
              placeholder="청첩장 푸터에 노출될 표시 문구. 어트리뷰션이 필요 없으면 비워둠"
            />
          </div>
          <div className={styles.formFull}>
            <Input label="태그 (쉼표 구분)" value={formTags} onChange={(e) => setFormTags(e.target.value)} placeholder="잔잔한, 피아노" />
          </div>
          <div className={styles.formFull}>
            <Textarea label="설명" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <Button variant="secondary" onClick={() => setEditingAsset(null)}>취소</Button>
          <Button onClick={handleSave} disabled={!formName || !formSlug || updateMut.isLoading}>수정</Button>
        </div>
      </Modal>
    </div>
  )
}
